import { Group, Line, Rect, Text } from 'react-konva';
import { IPosition2d } from '../../../../core/types/ui/position';
import { Size } from '../../../../core/types/ui/size';
import { UseSheetTheme } from '../../../hooks/useSheetTheme';
import { UseCellSelection } from '../../../hooks/useCellSelection';
import {
  AlignmentStyle,
  FillStyle,
  FontStyle,
  HorizontalAlignment,
  NumberFormatType,
  SelectionCell,
  Style,
  ValidationRule,
  VerticalAlignment,
} from '../../../types';
import { useMemo } from 'react';
import { RootState } from '../../../../core/redux/store';
import { createSelector } from '@reduxjs/toolkit';
// import { selectCellValue } from '../../../redux/sheetsManagerSlice';
import { connect } from 'react-redux';
import { convertPositionToAddress } from '../../../utils/address/singleAddress';
import {
  darkenColor,
  getBasicBackgroundColor,
  getBasicTextColor,
} from '../../../../core/utils/view';
import CellCheckbox from './CellCheckbox';
import { UseGridOperations } from '../../../hooks/useGridOperations';
import { isError } from 'lodash';
import { sheetConstants } from '../../../../windows/constants/sheetConstants';
import { formatCellVisibleText } from '../../../helpers/cellHelpers/cellHelpers';
import CellContents from './CellContents';
import { UseKonvaCursor } from '../../../../core/hooks/useKonvaCursor';

type CellVisibleStyle = Partial<FontStyle> &
  // Partial<Border> &
  Partial<AlignmentStyle> &
  Partial<FillStyle> &
  Partial<Pick<Style, 'textWrap' | 'rotation'>>;

interface CellProps
  extends Size,
    IPosition2d,
    UseKonvaCursor,
    Pick<UseCellSelection, 'onCellMouseDown' | 'onCellMouseHover'>,
    Pick<UseGridOperations, 'performSheetOperation'>,
    Pick<UseSheetTheme, 'stroke' | 'isDarkTheme' | 'isRtl'>, // 'textColor' 'fill' |
    CellVisibleStyle {
  sheetId: string;
  // colId: string;
  ci: number;
  // rowId: string;
  ri: number;
  frozen: boolean;
  zoom: number;

  numberFormatType?: NumberFormatType;
  numberFormatCode?: string;
  dataValidation?: ValidationRule;
  // startCi: number;
  // endCi: number;
  // startRi: number;
  // endRi: number;
}

interface MapProps extends CellProps {
  cellValue: string;
  isError: boolean;
}

const Cell: React.FC<MapProps> = ({
  x,
  y,
  width,
  height,
  frozen,

  ci,
  ri,

  // fill,
  stroke,
  isDarkTheme,

  cellValue,
  isRtl,

  isError,
  zoom,

  dataValidation,
  color,

  backgroundColor,

  onCellMouseDown,
  onCellMouseHover,
  performSheetOperation,
  onMouseEnter,
  onMouseLeave,

  ...props
}) => {
  let family = props.family ?? sheetConstants.cell.font.family;
  let horizontal =
    props.horizontal ?? isRtl
      ? HorizontalAlignment.Right
      : HorizontalAlignment.Left;
  // let color = props.color ?? getBasicTextColor(isDarkTheme);
  const size = (props.size ?? sheetConstants.cell.font.size) * zoom;
  const padding = zoom * 1.7;
  let formattedValue = cellValue;

  // const backgroundColor = props.backgroundColor

  const visibleBackgroundColor =
    backgroundColor ?? getBasicBackgroundColor(isDarkTheme);

  const renderBackground = () => {
    return (
      <Rect
        x={x}
        y={y}
        width={width}
        height={height}
        stroke={backgroundColor ? darkenColor(backgroundColor, 0.9) : stroke}
        fill={visibleBackgroundColor}
        hitStrokeWidth={0}
        strokeWidth={1 * zoom}
      />
    );
  };

  const renderCellContents = () => (
    <CellContents
      x={x}
      y={y}
      width={width}
      height={height}
      bold={!!props.bold}
      italic={!!props.italic}
      underline={!!props.underline}
      strikethrough={!!props.strikethrough}
      color={color}
      padding={padding}
      horizontal={horizontal}
      vertical={props.vertical ?? VerticalAlignment.Middle}
      cellValue={cellValue}
      formattedValue={formattedValue}
      backgroundColor={backgroundColor}
      size={size}
      isDarkTheme={isDarkTheme}
      family={family}
      isRtl={isRtl}
      ci={ci}
      ri={ri}
      zoom={zoom}
      dataValidation={dataValidation}
      // visibleBackgroundColor={visibleBackgroundColor}
      isError={isError}
      performSheetOperation={performSheetOperation}
      sheetId={props.sheetId}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  );

  return useMemo(() => {
    const output = formatCellVisibleText(isError, cellValue, width - padding, {
      type: props.numberFormatType,
      code: props.numberFormatCode,
    });
    if (output.str) formattedValue = output.str.toString();
    if (output.color) color = output.color;
    if (output.align) horizontal = output.align;
    // console.log(
    //   'cell',
    //   convertPositionToAddress({ ci: colIndex, ri: rowIndex })
    // );
    return (
      <Group
        clipX={x}
        clipY={y}
        clipHeight={height}
        clipWidth={width}
        onMouseDown={(e) =>
          onCellMouseDown(e, SelectionCell.Cell, {
            ci,
            ri,
          })
        }
        onMouseEnter={(e) =>
          !frozen &&
          onCellMouseHover(e, SelectionCell.Cell, {
            ci,
            ri,
          })
        }
      >
        {renderBackground()}
        {renderCellContents()}
        {/* {text()} */}
        {/* {cellCheckboxView()} */}

        {/* {isError && errorIndicator()} */}
      </Group>
    );
  }, [
    isDarkTheme,

    // fill,
    // stroke,
    // textColor,
    ci,
    ri,
    x,
    y,
    width,
    height,
    cellValue,
    isError,
    // startCi,
    // endCi,
    // startRi,
    // endRi,

    // Font
    props.bold,
    props.italic,
    props.underline,
    props.family,
    props.size,
    props.strikethrough,
    // props.outline,
    color,

    // Border
    // props.borderTop,
    // props.borderRight,
    // props.borderBottom,
    // props.borderLeft,
    // props.borderStyle,
    // props.borderColor,
    // props.borderWidth,

    // Fill
    backgroundColor,
    props.patternType,
    props.gradientColor,

    // Alignment
    props.vertical,
    props.horizontal,

    // Text
    props.textWrap,
    // props.indentation,
    props.rotation,

    dataValidation,

    props.numberFormatType,
    props.numberFormatCode,

    onCellMouseDown,
    onCellMouseHover,
  ]);
};

// const mapStateToProps = (state: RootState, props: CellProps) => {
//   const position = {
//     ci: props.ci,
//     ri: props.ri,
//   };

//   // const cellText = selectCellValue(state, props.sheetId, position);
//   // return {
//   //   ...cellText,
//   // };
//   return {
//     visibleValue: '',
//     isError: false,
//   };
// };

// const formatCellVisibleText = (
//   isError: boolean,
//   visibleValue: string,
//   width: number,
//   props: Pick<
//     CellProps,
//     'size' | 'family' | 'numberFormatCode' | 'numberFormatType'
//   >
// ): Partial<NumberFormatting.FormattedValue> => {
//   if (
//     isError ||
//     !visibleValue.length ||
//     !props.numberFormatCode ||
//     !props.numberFormatType
//   )
//     return {};

//   const result = NumberFormatting.applyFormat(
//     props.numberFormatType,
//     props.numberFormatCode,
//     visibleValue,
//     props.size ?? sheetConstants.cell.font.size,
//     props.family ?? sheetConstants.cell.font.family,
//     width
//   );

//   // console.log("result", result, visibleValue)

//   return result;
//   // const output: {
//   //   visibleValue?: string;
//   //   color?: string;
//   //   horizontal?: HorizontalAlignment;
//   // } = {};

//   // console.log(result);

//   // if (result.str) output.visibleValue = result.str?.toString();
//   // if (result.color) output.color = result.color;
//   // if (result.align) output.horizontal = result.align;

//   // return output;
// };

export default Cell; // connect(mapStateToProps)(Cell)
